import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useTestManagedAccountPositivePayConnection } from '@/api/hooks/managed-accounts/useTestManagedAccountPositivePayConnection';
import FormErrorSummary from '@/components/FormErrorSummary/FormErrorSummary';
import CpdActionButton from '@/components/ui/CpdActionButton';
import ConnectionTestResult from './components/ConnectionTestResult';
import PositivePayDeliverySection from './components/PositivePayDeliverySection';
import PositivePayEncryptionSection from './components/PositivePayEncryptionSection';
import PositivePayFileHeaderSection from './components/PositivePayFileHeaderSection';
import ServerDetailsSection from './components/ServerDetailsSection';
import SftpAuthenticationSection from './components/SftpAuthenticationSection';
import { usePositivePayConfigurationForm } from './hooks/usePositivePayConfigurationForm';

const PositivePayConfigurationForm = ({
  managedAccountId,
  configuration,
  onSaved,
}) => {
  const { t } = useTranslation();
  const {
    testing,
    testResult,
    testConnection,
    clearTestResult,
  } = useTestManagedAccountPositivePayConnection(managedAccountId);
  const formState = usePositivePayConfigurationForm({
    managedAccountId,
    configuration,
    onSaved: () => {
      clearTestResult();
      onSaved?.();
    },
  });
  const disabled = formState.saving || testing;

  const handleTestConnection = async () => {
    try {
      await testConnection();
    } catch {
      toast.error(t('managedAccounts.positivePay.test.failed'));
    }
  };

  return (
    <Box component='form' onSubmit={formState.save} noValidate>
      <FormErrorSummary errors={formState.generalErrors} />
      <ServerDetailsSection
        form={formState.form}
        fieldErrors={formState.fieldErrors}
        onChange={formState.updateField}
        disabled={disabled}
      />
      <SftpAuthenticationSection
        form={formState.form}
        fieldErrors={formState.fieldErrors}
        passwordConfigured={formState.passwordConfigured}
        privateKeyConfigured={formState.privateKeyConfigured}
        onFieldChange={formState.updateField}
        onModeChange={formState.updateAuthenticationMode}
        disabled={disabled}
      />
      <PositivePayDeliverySection
        form={formState.form}
        fieldErrors={formState.fieldErrors}
        onChange={formState.updateField}
        disabled={disabled}
      />
      <PositivePayFileHeaderSection
        form={formState.form}
        fieldErrors={formState.fieldErrors}
        headerAccountConfigured={formState.headerAccountConfigured}
        headerAccountLastFour={formState.headerAccountLastFour}
        onChange={formState.updateField}
        disabled={disabled}
      />
      <PositivePayEncryptionSection
        form={formState.form}
        fieldErrors={formState.fieldErrors}
        onShouldEncryptChange={formState.updateShouldEncrypt}
        onFieldChange={formState.updateField}
        disabled={disabled}
      />
      <ConnectionTestResult result={testResult} />
      <Box sx={{ display: 'flex', gap: 2 }}>
        <CpdActionButton
          type='button'
          variant='outlined'
          loading={testing}
          disabled={formState.saving || !formState.hasSavedConfiguration}
          sx={{
            '&&': { color: 'primary.main', borderColor: 'primary.main' },
            '&&:hover': {
              backgroundColor: 'action.hover',
              borderColor: 'primary.dark',
            },
            '&&.Mui-disabled': {
              color: 'text.disabled',
              borderColor: 'action.disabledBackground',
            },
          }}
          onClick={handleTestConnection}>
          {t('managedAccounts.test.action')}
        </CpdActionButton>
        <CpdActionButton
          type='submit'
          loading={formState.saving}
          disabled={testing}>
          {t('managedAccounts.positivePay.save')}
        </CpdActionButton>
      </Box>
    </Box>
  );
};

export default PositivePayConfigurationForm;
